import {
  formatMetric,
  formatVariance,
  formatVariancePct,
  type MetricFormat,
} from '@/components/Dashboard/financialsMockData'

export { formatMetric, formatVariance, formatVariancePct }
export type { MetricFormat }

export interface PlanFinancialsRow {
  label: string
  format: MetricFormat
  planned: number | null
  assortment: number | null
  variance: number | null
  variancePct: number | null
  // Sales-side rows: Planned shows '-' but $ Var / % Var are blank because no
  // plan baseline exists yet for a Draft plan.
  // Ratio rows (AUC, AUR, GM%, Disc%): Planned/$ Var/% Var are all blank —
  // only the Assortment value is meaningful.
  assortmentOnly?: boolean
  hideVariancePct?: boolean
}

// Canonical assortment numbers anchored to the design mockup
// (docs/screenshot in plan-finance.png). Sales-side ($, units, AUR/AUC) and
// receipt-side (Retail, FOB, Landed, units, IMU%, AUR) numbers are picked so
// the derived rows — Margin$, GM% — fall out cleanly without rounding drift.
interface BaseInputs {
  salesAmount: number
  costAmount: number
  salesUnits: number
  discPct: number
  retailAmount: number
  landedCost: number
  fobCost: number
  rcptUnits: number
  rcptImuPct: number
  rcptAur: number
}

const BASE_INPUTS: BaseInputs = {
  salesAmount: 854163,
  costAmount: 387182,
  salesUnits: 172290,
  discPct: 13.0,
  retailAmount: 1076000,
  landedCost: 701334,
  fobCost: 424228,
  rcptUnits: 188784,
  rcptImuPct: 60.6,
  rcptAur: 5.7,
}

function hashString(input: string): number {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function normalizedFromHash(seed: number, salt: string): number {
  return (hashString(`${seed}:${salt}`) % 10000) / 10000
}

function buildInputsForPlan(planId: string | null | undefined): BaseInputs {
  if (!planId) return BASE_INPUTS
  const seed = hashString(planId)

  // Per-plan scope shrink/grow on absolute values (some plans cover a small
  // SKU set, others a whole department). Stay within ±25% so the table still
  // reads as "the same business" plan-to-plan.
  const scale = 0.75 + normalizedFromHash(seed, 'scale') * 0.5 // 0.75x..1.25x

  // Small per-row noise so sales/receipts aren't a perfect multiple of scale.
  const noise = (salt: string, magnitude: number) =>
    1 + (normalizedFromHash(seed, salt) - 0.5) * magnitude

  return {
    salesAmount: Math.round(
      BASE_INPUTS.salesAmount * scale * noise('sa', 0.04)
    ),
    costAmount: Math.round(BASE_INPUTS.costAmount * scale * noise('ca', 0.04)),
    salesUnits: Math.round(BASE_INPUTS.salesUnits * scale * noise('su', 0.04)),
    discPct: BASE_INPUTS.discPct,
    retailAmount: Math.round(
      BASE_INPUTS.retailAmount * scale * noise('ra', 0.04)
    ),
    landedCost: Math.round(BASE_INPUTS.landedCost * scale * noise('lc', 0.04)),
    fobCost: Math.round(BASE_INPUTS.fobCost * scale * noise('fc', 0.04)),
    rcptUnits: Math.round(BASE_INPUTS.rcptUnits * scale * noise('ru', 0.04)),
    rcptImuPct: BASE_INPUTS.rcptImuPct,
    rcptAur: BASE_INPUTS.rcptAur,
  }
}

export function buildPlanFinancialsRows(
  planId: string | null | undefined
): PlanFinancialsRow[] {
  const v = buildInputsForPlan(planId)

  const margin = v.salesAmount - v.costAmount
  const gmPct = (margin / v.salesAmount) * 100
  const salesAur = v.salesAmount / v.salesUnits
  const salesAuc = v.costAmount / v.salesUnits

  // Sales-side rows: variance equals assortment (no plan baseline yet), but
  // % var is suppressed because dividing by an absent planned value reads as
  // noise in the sales section.
  // Ratio rows: only the assortment cell renders — everything else is blank.
  // Receipt-side rows: variance = assortment, % var = -100% (PBI convention).
  return [
    {
      label: 'Sales $',
      format: 'currency',
      planned: null,
      assortment: v.salesAmount,
      variance: v.salesAmount,
      variancePct: null,
      hideVariancePct: true,
    },
    {
      label: 'Cost $',
      format: 'currency',
      planned: null,
      assortment: v.costAmount,
      variance: v.costAmount,
      variancePct: null,
      hideVariancePct: true,
    },
    {
      label: 'Sales AUC',
      format: 'currency2dec',
      planned: null,
      assortment: salesAuc,
      variance: null,
      variancePct: null,
      assortmentOnly: true,
    },
    {
      label: 'Sales AUR',
      format: 'currency2dec',
      planned: null,
      assortment: salesAur,
      variance: null,
      variancePct: null,
      assortmentOnly: true,
    },
    {
      label: 'Sales Units',
      format: 'integer',
      planned: null,
      assortment: v.salesUnits,
      variance: v.salesUnits,
      variancePct: null,
      hideVariancePct: true,
    },
    {
      label: 'Margin$',
      format: 'currency',
      planned: null,
      assortment: margin,
      variance: margin,
      variancePct: null,
      hideVariancePct: true,
    },
    {
      label: 'GM%',
      format: 'percent',
      planned: null,
      assortment: gmPct,
      variance: null,
      variancePct: null,
      assortmentOnly: true,
    },
    {
      label: 'Disc%',
      format: 'percent',
      planned: null,
      assortment: v.discPct,
      variance: null,
      variancePct: null,
      assortmentOnly: true,
    },
    {
      label: 'Retail $',
      format: 'currency',
      planned: null,
      assortment: v.retailAmount,
      variance: v.retailAmount,
      variancePct: -1,
    },
    {
      label: 'Landed Cost $',
      format: 'currency',
      planned: null,
      assortment: v.landedCost,
      variance: v.landedCost,
      variancePct: -1,
    },
    {
      label: 'FOB Cost $',
      format: 'currency',
      planned: null,
      assortment: v.fobCost,
      variance: v.fobCost,
      variancePct: -1,
    },
    {
      label: 'Units',
      format: 'integer',
      planned: null,
      assortment: v.rcptUnits,
      variance: v.rcptUnits,
      variancePct: -1,
    },
    {
      label: 'Rcpt IMU %',
      format: 'percent',
      planned: null,
      assortment: v.rcptImuPct,
      variance: v.rcptImuPct,
      variancePct: -1,
    },
    {
      label: 'Rcpt AUR',
      format: 'currency2dec',
      planned: null,
      assortment: v.rcptAur,
      variance: v.rcptAur,
      variancePct: -1,
    },
  ]
}
